import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { ErrorCodes } from 'xosign-types';
import classNames from 'classnames';
import preloadChunks from '@/routes/preloadChunks.util';
import useApi from '@/hooks/useApi';
import useEmailFieldValidation from '@/hooks/useEmailFieldValidation';
import useLangFromQuery from '@/hooks/useLangFromQuery';
import useApp from '@/pages/App.context';
import initiateIDAustriaLogin from '../initiateIDAustriaLogin.utils';
import FormInput from '@/components/FormInput';
import FormError from '@/components/FormError';
import Button from '@/components/Button';
import IcIDAustriaKey from '@/assets/icons/IDAustriaKey.svg?react';
import IcIDAustriaText from '@/assets/icons/IDAustriaText.svg?react';
import styles from '@/pages/Auth/Auth.module.scss';
import FormPassword from '@/components/FormPassword';
import { getPasswordRules } from '@/utils/validation';
import ConsentCheckbox from '@/pages/Auth/ConsentCheckbox';

type RegisterInputs = {
  name: string;
  email: string;
  password: string;
};

const Register = () => {
  const { t, i18n } = useTranslation(['auth']);
  const { setAppTitle } = useApp();
  const navigate = useNavigate();
  const [apiRegister, { loading }] = useApi('users.register');
  useLangFromQuery();

  const [consentGiven, setConsentGiven] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<RegisterInputs>();

  const { emailRegistration, handleEmailBlur, handleEmailChange } =
    useEmailFieldValidation<RegisterInputs>({
      fieldName: 'email',
      register,
      requiredMessage: t('auth:fields.emailRequired'),
      patternMessage: t('auth:fields.emailValidity'),
      setValue,
      trigger,
    });

  const doRegister: SubmitHandler<RegisterInputs> = async (formInputs) => {
    if (!consentGiven) {
      return;
    }

    const res = await apiRegister({
      email: formInputs.email,
      name: formInputs.name,
      password: formInputs.password,
      language: i18n.language,
    });

    if (!res) {
      return;
    }

    if (res.error) {
      if (res.error.statusCode === 409) {
        setError('root', {
          message:
            res.error.response.code === ErrorCodes.EUserAlreadyExists
              ? t('auth:register.userRegistered')
              : res.error.response.message,
        });

        return;
      }
      setError('root', {
        message: res.error.response.message,
      });
      return;
    }

    if (res.response) {
      localStorage.setItem('token', res.response.token);

      // Not yet replacing the URL on purpose. This way we can always go back
      // to the beginning when the back button was clicked accidentally.
      void navigate('/auth/register/verify-email', {
        state: { email: formInputs.email },
      });
    }
  };

  const handleChange = () => {
    setConsentGiven((prevState) => {
      return !prevState;
    });
  };

  const handlePreload = () => {
    void preloadChunks['auth.verifyEmail']();
  };

  useEffect(() => {
    setAppTitle(t('auth:register.title'));
  }, [setAppTitle, t]);

  return (
    <>
      <h2 className={styles.heading}>{t('auth:register.title')}</h2>
      <div className={styles.text}>{t('auth:register.desc')}</div>
      <form onSubmit={handleSubmit(doRegister)} className={styles.form}>
        <FormInput
          autoComplete="name"
          errorMessage={errors.name?.message}
          id="name"
          label={t('auth:fields.name')}
          placeholder={t('auth:fields.namePlaceholder')}
          {...register('name', { required: t('auth:fields.nameRequired') })}
        />
        <FormInput
          autoComplete="email"
          errorMessage={errors.email?.message}
          id="email"
          label={t('auth:fields.email')}
          placeholder={t('auth:fields.emailPlaceholder')}
          type="email"
          {...emailRegistration}
          onBlur={handleEmailBlur}
          onChange={handleEmailChange}
        />
        {errors.root?.message && (
          <FormError isRoot message={errors.root.message} />
        )}
        <FormPassword
          autoComplete="new-password"
          id="password"
          label={t('auth:password.newPassword')}
          placeholder={t('auth:password.newPasswordPlaceholder')}
          withRequirements="newPassword"
          {...register('password', getPasswordRules(t))}
        />
        {errors.root?.message && (
          <FormError isRoot message={errors.root.message} id="root-error" />
        )}
        <ConsentCheckbox checked={consentGiven} onToggle={handleChange} />
        <Button
          className={styles.button}
          submit
          loading={loading}
          onMouseEnter={handlePreload}
          onFocus={handlePreload}
          disabled={!consentGiven}
        >
          {t('auth:register.submit')}
        </Button>
        <div className={styles.divider}>{t('auth:login.separator')}</div>
        <Button
          className={classNames(
            styles.button,
            styles.external,
            styles.idAustria
          )}
          loading={loading}
          variant="secondary"
          onClick={initiateIDAustriaLogin}
          aria-label={t('auth:login.button.external', {
            name: 'IDAustria',
          })}
        >
          <IcIDAustriaKey className={styles.idAustriaLogo} />
          <IcIDAustriaText className={styles.idAustriaText} />
        </Button>

        <div className={styles.switchToOther}>
          <Trans i18nKey="auth:register.switchQuestion">
            <Link to="/auth/login" className={styles.link} />
          </Trans>
        </div>
      </form>
    </>
  );
};

export default Register;
